import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/cn';

interface TopBarProps {
  /** Page title */
  title: string;
  /** Optional subtitle / breadcrumb text */
  subtitle?: string;
  /** Show back button */
  showBack?: boolean;
  /** Back navigation path (defaults to browser back) */
  backTo?: string;
  /** Custom back handler — if provided, overrides backTo / browser-back. */
  onBack?: () => void;
  /** Right-side action buttons */
  actions?: React.ReactNode;
  /** Additional className */
  className?: string;
}

export function TopBar({
  title,
  subtitle,
  showBack = false,
  backTo,
  onBack,
  actions,
  className,
}: TopBarProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <header
      className={cn(
        'flex items-center justify-between h-14 px-8',
        'border-b border-slate-200/80 bg-white/60 backdrop-blur-sm',
        'sticky top-0 z-30',
        className,
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {showBack && (
          <button
            onClick={handleBack}
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-lg',
              'text-slate-400 hover:text-slate-700 hover:bg-slate-100',
              'transition-colors duration-150',
            )}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}

        <div className="min-w-0">
          <h1 className="text-[15px] font-semibold text-slate-900 truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xs text-slate-500 truncate">{subtitle}</p>
          )}
        </div>
      </div>

      {actions && (
        <div className="flex items-center gap-2 shrink-0">{actions}</div>
      )}
    </header>
  );
}
